import type { DocNode } from '@atlaskit/adf-schema';
import { reduce, traverse } from '@atlaskit/adf-utils/traverse';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

type StreamEvent = {
	adfResponse?: DocNode | DocNode['content'][number];
	cacheKey?: string;
	markdown?: string;
	modelInput?: object;
	type: string;
};

export interface Ramp {
	flushDelaySec: number;
	growthFactor: number;
	initialDelaySec: number;
	stepDelaySec: number;
}

export interface StreamFlowPacerConfig {
	/**
	 * How often (in milliseconds) the backlog-based deadline adjustment runs.
	 * On each interval the pacer recomputes `charsPerTick` so that the current
	 * backlog can drain within `targetCompletionMs`. This adjustment runs
	 * independently of whether the terminal/complete event has arrived, so the
	 * pacer keeps up while the backend is still streaming and finishes promptly
	 * once it stops.
	 *
	 * Only used when the `platform_editor_ai_streaming_backlog_pacing` gate is on.
	 * Defaults to 1000ms when not provided.
	 */
	adjustIntervalMs?: number;
	backlogRampThreshold: number;
	initialCharsPerTick: number;
	maxCharsPerTick: number;
	ramp: Ramp;
	/**
	 * Target window (in milliseconds) within which the current backlog should be
	 * fully drained. Used by the backlog-based deadline adjustment to derive the
	 * minimum required `charsPerTick`.
	 *
	 * Only used when the `platform_editor_ai_streaming_backlog_pacing` gate is on.
	 * Defaults to 2000ms when not provided.
	 */
	targetCompletionMs?: number;
	tickMs: number;
}

const DEFAULT_TARGET_COMPLETION_MS = 2000;
const DEFAULT_ADJUST_INTERVAL_MS = 1000;

/**
 * Context passed to `onPacerCompletionGap` handlers, describing the lag between
 * the backend finishing its stream and the pacer finishing draining it.
 */
export interface PacerCompletionGapInfo {
	/**
	 * Time (in milliseconds) between the backend pushing its terminal event and
	 * the pacer flushing it to the editor.
	 */
	backendEndToPacerCompleteMs: number;
	/**
	 * Backlog (in characters) that was still undelivered at the moment the
	 * backend's terminal event arrived.
	 */
	backlogCharsAtBackendEnd: number;
	/**
	 * `charsPerTick` value in effect when the pacer completed.
	 */
	charsPerTickAtComplete: number;
	/**
	 * The terminal event type that completed the stream (e.g. `complete`).
	 */
	terminalType: string;
}

class Count {
	constructor(
		readonly chars: number,
		readonly length: number,
	) {}

	eq(other: Count): boolean {
		return this.chars === other.chars && this.length === other.length;
	}

	sub(other: Count): Count {
		return new Count(this.chars - other.chars, this.length - other.length);
	}

	gte(other: Count): boolean {
		return this.chars >= other.chars && this.length >= other.length;
	}
}

/**
 * TODO: EDITOR-1533 - Improve this class and make it more generic.
 */
export class StreamFlowPacer {
	readonly push = (evt: StreamEvent): void => {
		if (evt.type === 'stream') {
			this.streamEvt = evt;
		} else {
			this.terminalEvt = evt;
			// Record when the backend finished streaming (terminal event arrived) so we
			// can measure the gap until the pacer actually finishes draining. Only the
			// first terminal event is recorded for a given stream.
			if (this.terminalPushedAt === null) {
				this.terminalPushedAt = Date.now();
				this.backlogCharsAtTerminal = this.backlog().chars;
			}
		}
	};

	constructor(private readonly cfg: StreamFlowPacerConfig) {
		this.charsPerTick = cfg.initialCharsPerTick;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	setEmit(emit: (evt: any) => void): void {
		this.emit = emit;
	}

	setTickMs(tickMs: number): void {
		this.cfg.tickMs = tickMs;
	}

	setCharsPerTick(charsPerTick: number): void {
		this.charsPerTick = charsPerTick;
	}

	setMaxCharsPerTick(maxCharsPerTick: number): void {
		this.cfg.maxCharsPerTick = maxCharsPerTick;
	}

	/**
	 * you need to call setEmit before calling start
	 *
	 * @example
	 * streamFlowPacer.setEmit();
	 * streamFlowPacer.start();
	 */
	start(): void {
		if (this.destroyed) {
			return;
		}

		if (this.emit) {
			this.timer = setInterval(() => this.tick(), this.cfg.tickMs);
		}
	}

	pause(): void {
		this.paused = true;
		this.stop();
	}

	resume(): void {
		this.paused = false;
		this.start();
	}

	destroy(): void {
		this.reset();
		this.destroyed = true;
	}

	onComplete(handler: () => void): void {
		this.onCompleteHandlers.push(handler);
	}

	/**
	 * Register a handler that fires exactly once per stream, when the pacer
	 * finishes draining and emits a `complete` / `response too similar` terminal
	 * event. The handler receives the timing gap (in milliseconds) between when
	 * the backend finished streaming (the terminal event was pushed) and when the
	 * pacer actually finished draining, plus useful context for analytics.
	 */
	onPacerCompletionGap(handler: (info: PacerCompletionGapInfo) => void): void {
		this.onPacerCompletionGapHandlers.push(handler);
	}

	reset(): void {
		this.stop();
		this.emit = null;
		this.streamEvt = null;
		this.terminalEvt = null;
		this.charsPerTick = this.cfg.initialCharsPerTick;
		this.delivered = new Count(0, 0);
		this.prevTickTime = 0;
		this.currTickTime = 0;
		this.ticksSinceRamp = 0;
		this.ramped = 0;
		this.paused = false;
		this.destroyed = false;
		this.onCompleteHandlers.length = 0;
		this.onPacerCompletionGapHandlers.length = 0;
		this.ticksSinceAdjust = 0;
		this.hasAdjusted = false;
		this.terminalPushedAt = null;
		this.backlogCharsAtTerminal = 0;
	}

	isRunning(): boolean {
		return this.timer !== null;
	}

	private timer: ReturnType<typeof setInterval> | null = null;
	private streamEvt: StreamEvent | null = null;
	private terminalEvt: StreamEvent | null = null;
	private charsPerTick: number;
	private delivered: Count = new Count(0, 0);
	private prevTickTime = 0;
	private currTickTime = 0;
	private ticksSinceRamp = 0;
	private ramped = 0;
	private paused = false;
	private destroyed = false;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private emit: ((evt: any) => void) | null = null;
	private onCompleteHandlers: Array<() => void> = [];
	private onPacerCompletionGapHandlers: Array<(info: PacerCompletionGapInfo) => void> = [];
	private processedSyncBlockLocalId: (string | null)[] = [];
	// Number of ticks since the backlog-based deadline adjustment last ran.
	private ticksSinceAdjust = 0;
	// Whether the backlog-based deadline adjustment has run at least once for the
	// current stream (so the first adjustment can fire immediately).
	private hasAdjusted = false;
	// Timestamp (ms) when the backend's terminal event was first pushed. `null`
	// until a terminal event arrives.
	private terminalPushedAt: number | null = null;
	// Backlog (chars) at the moment the backend's terminal event arrived.
	private backlogCharsAtTerminal = 0;

	private stop() {
		if (this.timer) {
			clearInterval(this.timer);
			this.timer = null;
		}
	}

	private tick() {
		this.prevTickTime = this.currTickTime;
		this.currTickTime = Date.now();

		if (this.paused || !(this.streamEvt || this.terminalEvt)) {
			return;
		}

		if (fg('platform_editor_ai_streaming_backlog_pacing')) {
			this.maybeAdjustForBacklog();
		} else {
			this.maybeRamp();
		}
		this.deliverChunk();
		this.maybeFlushTerminal();
	}

	/**
	 * Backlog-driven pacing. On a fixed cadence (`adjustIntervalMs`, default 1s)
	 * recompute the minimum `charsPerTick` needed to drain the current backlog
	 * within `targetCompletionMs` (default 2s) and raise `charsPerTick` to it.
	 *
	 * This runs independently of whether the terminal/complete event has arrived,
	 * so the pacer keeps up while the backend is still streaming fast and finishes
	 * promptly once the backend stops. `charsPerTick` is never decreased, so a
	 * normal/slow stream keeps its smooth typewriter feel.
	 */
	private maybeAdjustForBacklog() {
		const adjustIntervalMs = this.cfg.adjustIntervalMs ?? DEFAULT_ADJUST_INTERVAL_MS;
		const ticksPerAdjust = Math.max(1, Math.round(adjustIntervalMs / this.cfg.tickMs));

		// Adjust on the very first eligible tick (so we start converging immediately
		// rather than waiting a whole interval), then on every `ticksPerAdjust`
		// cadence thereafter.
		if (this.hasAdjusted && this.ticksSinceAdjust < ticksPerAdjust) {
			this.ticksSinceAdjust++;
			return;
		}
		this.ticksSinceAdjust = 0;
		this.hasAdjusted = true;

		const required = this.computeDeadlineCharsPerTick();
		if (required > this.charsPerTick) {
			this.charsPerTick = required;
		}
	}

	/**
	 * Minimum `charsPerTick` required to drain the current backlog within
	 * `targetCompletionMs`, based on how many ticks fit in that window.
	 */
	private computeDeadlineCharsPerTick(): number {
		const backlogChars = this.backlog().chars;
		if (backlogChars <= 0) {
			return this.charsPerTick;
		}
		const targetCompletionMs = this.cfg.targetCompletionMs ?? DEFAULT_TARGET_COMPLETION_MS;
		const ticksRemaining = Math.max(1, Math.floor(targetCompletionMs / this.cfg.tickMs));
		return Math.ceil(backlogChars / ticksRemaining);
	}

	private deliverChunk() {
		if (!this.streamEvt) {
			return;
		}

		const total = this.semanticLen(this.streamEvt);
		if (this.delivered.gte(total)) {
			return;
		}

		let slice: StreamEvent | null;

		if (
			this.prevTickTime > 0 &&
			this.currTickTime > 0 &&
			this.currTickTime - this.prevTickTime >= 3000
		) {
			// If it's been more than 3 seconds since last tick,
			// assume the tab is in background, so we flush everything
			slice = this.sliceStreamEvt(this.streamEvt, -1);
		} else if (this.delivered.chars >= total.chars && this.delivered.length < total.length) {
			// No more text chars can be found we should flush the entire latest response
			slice = this.sliceStreamEvt(this.streamEvt, -1);
		} else {
			slice = this.sliceStreamEvt(
				this.streamEvt,
				Math.min(this.delivered.chars + this.charsPerTick, total.chars),
			);
		}

		if (!slice) {
			return;
		}

		this.emit?.(slice);
		this.delivered = this.semanticLen(slice);

		if (
			this.delivered.eq(total) &&
			!!this.onCompleteHandlers.length &&
			!expValEquals('platform_editor_fix_ai_streaming_race', 'isEnabled', true)
		) {
			this.onCompleteHandlers.forEach((handler) => handler());
			this.onCompleteHandlers.length = 0;
		}
	}

	private canFlushTerminal(): boolean {
		if (!this.terminalEvt) {
			return false;
		}

		// For "complete" we must wait until every char is flushed
		// For mock, the final response can be less than the sum of previous responses, so backlog can be negative
		if (this.terminalEvt.type === 'complete' || this.terminalEvt.type === 'response too similar') {
			return this.backlog().chars <= 0;
		}

		// All other terminal kinds (error, abort, etc.) bypass the backlog
		return true;
	}

	private maybeFlushTerminal(): void {
		if (!this.canFlushTerminal()) {
			return;
		}

		if (this.terminalEvt) {
			const terminalEvt = this.terminalEvt;
			this.emit?.(terminalEvt);
			if (expValEquals('platform_editor_fix_ai_streaming_race', 'isEnabled', true)) {
				this.onCompleteHandlers.forEach((handler) => handler());
				this.onCompleteHandlers.length = 0;
			}
			this.firePacerCompletionGap(terminalEvt);
			this.destroy();
		}
	}

	/**
	 * Fire the completion-gap analytics callback once, capturing the lag between
	 * the backend finishing its stream and the pacer finishing draining it. Must
	 * be called before `destroy()`/`reset()` clears the captured timing state.
	 */
	private firePacerCompletionGap(terminalEvt: StreamEvent): void {
		if (!this.onPacerCompletionGapHandlers.length || this.terminalPushedAt === null) {
			return;
		}

		const info: PacerCompletionGapInfo = {
			backendEndToPacerCompleteMs: Date.now() - this.terminalPushedAt,
			backlogCharsAtBackendEnd: this.backlogCharsAtTerminal,
			charsPerTickAtComplete: this.charsPerTick,
			terminalType: terminalEvt.type,
		};

		this.onPacerCompletionGapHandlers.forEach((handler) => handler(info));
		this.onPacerCompletionGapHandlers.length = 0;
	}

	private maybeRamp() {
		if (!this.shouldRamp()) {
			this.ticksSinceRamp++;
			return;
		}
		this.charsPerTick = Math.min(
			Math.ceil(this.charsPerTick * this.cfg.ramp.growthFactor),
			this.cfg.maxCharsPerTick,
		);
		this.ticksSinceRamp = 0;
		this.ramped++;
	}

	private shouldRamp() {
		const wait = this.secondsToTicks(
			this.getRampDelay(this.cfg.ramp, this.ramped, this.terminalEvt),
		);
		return (
			(this.backlog().chars > this.cfg.backlogRampThreshold ||
				this.terminalEvt?.type === 'response too similar') &&
			this.ticksSinceRamp >= wait &&
			this.charsPerTick < this.cfg.maxCharsPerTick
		);
	}

	private backlog() {
		if (!this.streamEvt) {
			return new Count(0, 0);
		}
		const result = this.semanticLen(this.streamEvt);
		return result.sub(this.delivered);
	}

	private getRampDelay(rampConfig: Ramp, rampedCnt: number, response?: StreamEvent | null): number {
		if (response?.type === 'response too similar') {
			return rampConfig.flushDelaySec;
		}
		return rampedCnt > 0 ? rampConfig.stepDelaySec : rampConfig.initialDelaySec;
	}

	private secondsToTicks(sec: number) {
		return (sec * 1000) / this.cfg.tickMs;
	}

	private semanticLen(evt: StreamEvent) {
		if (!!evt.markdown && typeof evt.markdown === 'string') {
			return new Count(evt.markdown.length, evt.markdown.length);
		}

		return this.adfTextLen(evt.adfResponse);
	}

	/**
	 *
	 * @param evt
	 * @param chars
	 * @returns
	 */
	private sliceStreamEvt(evt: StreamEvent, chars: number): StreamEvent | null {
		if (evt.adfResponse) {
			if (chars < 0) {
				return {
					...evt,
					adfResponse: this.filterIncompleteNodes(evt.adfResponse),
				};
			}
			return { ...evt, adfResponse: this.truncateAdf(evt.adfResponse, chars) };
		}
		if (evt.markdown?.length) {
			return { ...evt, markdown: evt.markdown.slice(0, chars < 0 ? undefined : chars) };
		}
		return null;
	}

	public adfTextLen(root: ADFEntity | null | undefined): Count {
		if (!root) {
			return new Count(0, 0);
		}

		const { chars, length } = reduce<{ chars: number; length: number }>(
			root,
			(result, node) =>
				node.type === 'text' && typeof node.text === 'string'
					? { chars: result.chars + node.text.length, length: result.length + node.text.length + 2 }
					: { chars: result.chars, length: result.length + 2 },
			{
				chars: 0,
				length: 0,
			},
		);
		return new Count(chars, length);
	}

	/**
	 * Sometimes the localID is a UUID with 36 characters (8-4-4-4-12 with dashes).
	 * Sometimes it's a 12 character string.
	 * So we validate the length and the presence of dashes.
	 */
	private static isValidLocalId(value: unknown): boolean {
		return (
			typeof value === 'string' && (value.includes('-') ? value.length >= 36 : value.length === 12)
		);
	}

	/**
	 * Lightweight filter for the flush path (chars < 0).
	 * Drops bodiedSyncBlock nodes with invalid localId.
	 * This is used to ensure the structure is not emitted until the localId is complete.
	 */
	private filterIncompleteNodes(root: ADFEntity): DocNode {
		return traverse(root, {
			bodiedSyncBlock: (node) => {
				if (node.type === 'bodiedSyncBlock') {
					if (!this.processedSyncBlockLocalId.includes(node.attrs?.localId)) {
						return false;
					}
					return;
				}
			},
		}) as DocNode;
	}

	private truncateAdf(root: ADFEntity, budget: number): DocNode {
		let remaining = Math.max(0, budget);
		/**
		 * Some older browsers like chrome 97 doesn't support structuredClone.
		 * So using JSON.parse(JSON.stringify()) as a fallback.
		 */
		const clone =
			typeof window !== 'undefined' && typeof window.structuredClone === 'function'
				? structuredClone(root)
				: JSON.parse(JSON.stringify(root));

		let lastTableRowLocalId: string;
		let remainingMergedRows = 1;

		return traverse(clone, {
			any: (node, parent) => {
				//Drop bodiedSyncBlock nodes with invalid localId.
				if (node.type === 'bodiedSyncBlock') {
					if (!this.processedSyncBlockLocalId.includes(node.attrs?.localId)) {
						//if it has an invalid local id, we mark it as pending
						if (!StreamFlowPacer.isValidLocalId(node.attrs?.localId)) {
							return false;
						} else {
							this.processedSyncBlockLocalId.push(node.attrs?.localId);
							return;
						}
						// we've seen this before, we are emitting it
					} else {
						return;
					}
				}

				if (node.type === 'tableCell' || node.type === 'tableHeader') {
					// merge information is stored inside cells, so we keep track of the largest rowspan we encounter
					remainingMergedRows = Math.max(remainingMergedRows, node.attrs?.rowspan || 1);
				}
				// We subtract the 'merged rows' count as we encounter rows
				if (remainingMergedRows > 0 && node.type === 'tableRow') {
					remainingMergedRows--;
				}

				if (node.type === 'tableRow') {
					/**
					 * Upon parsing a table row, we mark it as seen using the localId
					 * so we can check when descending into the node whether to keep the row structure
					 * or drop it entirely (if no chars are remaining when entering a table row)
					 */
					lastTableRowLocalId = node.attrs?.localId;
				}

				if (remaining === 0) {
					if (node.type === 'tableRow') {
						if (remainingMergedRows === 0) {
							return false;
						}
						return;
					}
					let ancestor: typeof parent | undefined = parent;
					while (ancestor) {
						if (ancestor?.node?.type === 'tableRow') {
							if (
								// if we're in the middle of a table row that has been parsed, we want to keep the rest of the cells
								ancestor.node?.attrs?.localId === lastTableRowLocalId ||
								// if there are still merged rows to be processed, we want to keep the following rows and cells too
								remainingMergedRows > 0
							) {
								if (node.type === 'tableCell' || node.type === 'tableHeader') {
									/**
									 * We return all cells in the current row that's being streamed to
									 * ensure the table row structure is maintained.
									 * We also keep table cell structures if there are still merged rows to be processed.
									 */
									return {
										...node,
										content: [
											{
												type: 'paragraph',
												content: [],
											},
										],
									};
								}

								// keep empty paragraphs to make tableCell and tableHeader valid
								if (node.type === 'paragraph' && node.content?.length === 0) {
									return;
								}

								return false;
							}
							return false;
						}
						ancestor = ancestor.parent;
					}
					return false;
				}

				if (node.type !== 'text' || typeof node.text !== 'string') {
					return;
				}

				if (node.text.length <= remaining) {
					remaining -= node.text.length;
					return;
				}
				const text = node.text.slice(0, remaining);
				remaining = 0;
				return { ...node, text };
			},
		}) as DocNode;
	}
}
